import React, { useMemo, useEffect } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { Text } from 'v3/components';

export default function CreateAdjustment({
  groups = [],
  originGroups = null,
  reasons = [],
  form,
  setForm,
  onSelectGroup,
  groupError = null,
  travelStatus,
  endLoadingOrder,
  operationSlug = 'OPERATION',
  errors = {},
  onFieldChange,
  reasonLoading,
  submitError = null,
}) {
  const noGroups  = !groups?.length;
  const originOpts = originGroups ? originGroups : [];
  const noOrigin   = !originOpts?.length;
  const noReasons  = !reasons?.length;

  const handleField = (field) => (e) => {
    const raw = e && e.target ? e.target.value : e;
    const value = (field === 'reasonId' || field === 'fromGroupId')
      ? (raw === '' ? '' : Number(raw))
      : raw;

    if (typeof onFieldChange === 'function') onFieldChange(field, value);
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const isAwaitingEmission = useMemo(() => {
    const code = String(travelStatus?.code || '').toUpperCase();
    const name = String(travelStatus?.name || '').toLowerCase();
    return code === 'ST9' || code === 'AWAITING_EMISSION' || name.includes('aguardando emissão');
  }, [travelStatus]);

  const shouldLockToOperation = useMemo(() => {
    const tsOrder = Number(travelStatus?.order ?? NaN);
    const endOrder = Number(endLoadingOrder ?? NaN);
    if (!Number.isFinite(tsOrder) || !Number.isFinite(endOrder)) return false;
    const beforeOrEqualEndLoading = tsOrder <= endOrder;
    return beforeOrEqualEndLoading && !isAwaitingEmission;
  }, [travelStatus, endLoadingOrder, isAwaitingEmission]);

  useEffect(() => {
    if (shouldLockToOperation && form?.groupSlug !== operationSlug) {
      if (typeof onSelectGroup === 'function') onSelectGroup(operationSlug);
      setForm(prev => ({ ...prev, groupSlug: operationSlug, reasonId: '' }));
      if (typeof onFieldChange === 'function') {
        onFieldChange('groupSlug', operationSlug);
        onFieldChange('reasonId', '');
      }
    }
  }, [shouldLockToOperation, operationSlug]);

  const groupOptions = useMemo(() => {
    if (shouldLockToOperation) return [{ slug: operationSlug, name: 'Operação' }];
    return groups;
  }, [shouldLockToOperation, groups, operationSlug]);

  return (
    <div>
      <div className="mb-3">
        <Text type="header" color="dark">Inclusão de pendência setores</Text>
      </div>

      {submitError && (
        <div className="alert alert-danger py-2 px-3 mb-3">
          {submitError}
        </div>
      )}

      <Row>
        <Col md={4} xs={12}>
          <Form.Group controlId="fromGroup">
            <Form.Label>Setor de origem</Form.Label>
            <Form.Control
              as="select"
              value={form?.fromGroupId ?? ''}
              onChange={handleField('fromGroupId')}
              disabled={noOrigin}
              isInvalid={!!errors.fromGroupId}
            >
              <option value="">Selecione…</option>
              {originOpts.map(g => (
                <option key={g.id ?? g.slug} value={g.id ?? ''}>
                  {g.name ?? g.slug}
                </option>
              ))}
            </Form.Control>
            <Form.Control.Feedback type="invalid">
              {errors.fromGroupId}
            </Form.Control.Feedback>
            {noOrigin && (
              <small className="text-muted d-block mt-1">
                Nenhum setor de origem disponível.
              </small>
            )}
          </Form.Group>
        </Col>
        <Col md={4} xs={12}>
          <Form.Group controlId="destGroup">
            <Form.Label>Setor destino</Form.Label>
            <Form.Control
              as="select"
              value={form?.groupSlug ?? ''}
              onChange={(e) => {
                if (shouldLockToOperation) return;
                const value = e?.target?.value ?? '';
                if (typeof onFieldChange === 'function') onFieldChange('groupSlug', value);
                if (typeof onSelectGroup === 'function') onSelectGroup(value);
                setForm(prev => ({ ...prev, groupSlug: value, reasonId: '' }));
              }}
              disabled={noGroups || shouldLockToOperation}
              isInvalid={!!errors.groupSlug}
            >
              {!shouldLockToOperation && <option value="">Selecione…</option>}
              {groupOptions.map(g => (
                <option key={g.slug} value={g.slug}>{g.name ?? g.slug}</option>
              ))}
            </Form.Control>
            <Form.Control.Feedback type="invalid">
              {errors.groupSlug}
            </Form.Control.Feedback>

            {noGroups && (
              <small className="text-muted d-block mt-1">
                Nenhum setor disponível. Verifique autenticação/permissões.
              </small>
            )}
            {groupError && form?.groupSlug && (
              <small className="text-danger d-block mt-1">{groupError}</small>
            )}
          </Form.Group>
        </Col>
        <Col md={4} xs={12}>
          <Form.Group controlId="reason">
            <Form.Label>Motivo</Form.Label>
            <Form.Control
              as="select"
              value={form?.reasonId ?? ''}
              onChange={handleField('reasonId')}
              disabled={!form?.groupSlug || noReasons || !!groupError || reasonLoading}
              isInvalid={!!errors.reasonId}
            >
              <option value="">
                {!form?.groupSlug
                  ? 'Selecione o setor destino primeiro…'
                  : groupError
                  ? 'Setor indisponível…'
                  : noReasons
                  ? 'Sem motivos disponíveis…'
                  : 'Selecione…'}
              </option>
              {reasons.map(r => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </Form.Control>
            <Form.Control.Feedback type="invalid">
              {errors.reasonId}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <Form.Group controlId="desc">
            <Form.Label>Descrição</Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              value={form?.description ?? ''}
              onChange={handleField('description')}
              placeholder="Descreva a pendência…"
              isInvalid={!!errors.description}
            />
            <Form.Control.Feedback type="invalid">
              {errors.description}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
}
